import { useMemo } from 'react';

import { AssignmentsViewOptions } from 'src/interfaces/assignment.types';
import { getAmpConfigFlags } from 'src/services/amp-config.service';
import authService from 'src/services/api/auth.service';
import {
  useActiveAssignmentsViewSelector,
  useActiveMSAssignmentsWorkType,
} from 'src/store/features';

import {
  useK5PastWorkStudentAssignments,
  useK5ToDoStudentAssignments,
  useMSStudentAssignments,
} from './student-assignments';
import { useMSStudentAssignmentsByWorkType } from './student-assignments/use-ms-student-assignments-by-work-type';
import {
  useK5PastWorkTeacherAssignments,
  useK5ToDoTeacherAssignments,
  useMSTeacherAssignments,
} from './teacher-assignments';
import { useMSTeacherAssignmentsByWorkType } from './teacher-assignments/use-ms-teacher-assignments-by-work-type';
import { useIsMiddleSchoolView } from './use-is-middle-school-view';

export const useActiveAssignments = () => {
  const isStudent = authService.isStudent();

  const activeAssignmentsView = useActiveAssignmentsViewSelector();
  const activeMSAssignmentsWorkType = useActiveMSAssignmentsWorkType();
  const isMiddleSchoolView = useIsMiddleSchoolView();

  // TODO: Remove the feature flag after RFS.
  const isUpdatedMSViewEnabled = getAmpConfigFlags().isUpdatedMSViewEnabled;

  const {
    refetch: refetchMSTeacherAssignments,
    status: teacherMSAssignmentsStatus,
    data: teacherMSAssignments,
  } = useMSTeacherAssignments();
  const {
    refetch: refetchMSStudentAssignments,
    status: studentMSAssignmentsStatus,
    data: studentMSAssignments,
  } = useMSStudentAssignments();
  const {
    refetch: refetchMSStudentAssignmentsByWorkType,
    data: studentAssignments,
    status: studentMSWorkTypeAssignmentsStatus,
  } = useMSStudentAssignmentsByWorkType(activeMSAssignmentsWorkType);
  const {
    refetch: refetchMSTeacherAssignmentsByWorkType,
    data: teacherAssignments,
    status: teacherMSWorkTypeAssignmentsStatus,
  } = useMSTeacherAssignmentsByWorkType(activeMSAssignmentsWorkType);
  const {
    refetch: refetchK5ToDoStudentAssignments,
    status: k5ToDoStudentAssignmentsStatus,
    data: k5ToDoStudentAssignments,
  } = useK5ToDoStudentAssignments();
  const {
    refetch: refetchK5ToDoTeacherAssignments,
    status: k5ToDoTeacherAssignmentsStatus,
    data: k5ToDoTeacherAssignments,
  } = useK5ToDoTeacherAssignments();
  const {
    refetch: refetchK5PastWorkTeacherAssignments,
    status: k5PastWorkTeacherAssignmentsStatus,
    data: k5PastWorkTeacherAssignments,
  } = useK5PastWorkTeacherAssignments();
  const {
    refetch: refetchK5PastWorkStudentAssignments,
    status: k5PastWorkStudentAssignmentsStatus,
    data: k5PastWorkStudentAssignments,
  } = useK5PastWorkStudentAssignments();

  return useMemo(() => {
    if (isMiddleSchoolView && isUpdatedMSViewEnabled) {
      return isStudent
        ? {
            assignmentsStatus: studentMSWorkTypeAssignmentsStatus,
            refetch: refetchMSStudentAssignmentsByWorkType,
            data: studentAssignments,
          }
        : {
            assignmentsStatus: teacherMSWorkTypeAssignmentsStatus,
            refetch: refetchMSTeacherAssignmentsByWorkType,
            data: teacherAssignments,
          };
    }

    if (isMiddleSchoolView) {
      return isStudent
        ? {
            assignmentsStatus: studentMSAssignmentsStatus,
            refetch: refetchMSStudentAssignments,
            data: studentMSAssignments,
          }
        : {
            assignmentsStatus: teacherMSAssignmentsStatus,
            refetch: refetchMSTeacherAssignments,
            data: teacherMSAssignments,
          };
    }

    if (activeAssignmentsView === AssignmentsViewOptions.TODO) {
      return isStudent
        ? {
            assignmentsStatus: k5ToDoStudentAssignmentsStatus,
            refetch: refetchK5ToDoStudentAssignments,
            data: k5ToDoStudentAssignments,
          }
        : {
            assignmentsStatus: k5ToDoTeacherAssignmentsStatus,
            refetch: refetchK5ToDoTeacherAssignments,
            data: k5ToDoTeacherAssignments,
          };
    }

    return isStudent
      ? {
          assignmentsStatus: k5PastWorkStudentAssignmentsStatus,
          refetch: refetchK5PastWorkStudentAssignments,
          data: k5PastWorkStudentAssignments,
        }
      : {
          assignmentsStatus: k5PastWorkTeacherAssignmentsStatus,
          refetch: refetchK5PastWorkTeacherAssignments,
          data: k5PastWorkTeacherAssignments,
        };
    // Avoid including the re-fetching methods in the deps as they are redundant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeAssignmentsView,
    isMiddleSchoolView,
    isStudent,
    k5PastWorkStudentAssignments,
    k5PastWorkStudentAssignmentsStatus,
    k5PastWorkTeacherAssignments,
    k5PastWorkTeacherAssignmentsStatus,
    k5ToDoStudentAssignments,
    k5ToDoStudentAssignmentsStatus,
    k5ToDoTeacherAssignments,
    k5ToDoTeacherAssignmentsStatus,
    studentMSAssignments,
    studentMSAssignmentsStatus,
    teacherMSAssignments,
    teacherMSAssignmentsStatus,
    teacherMSWorkTypeAssignmentsStatus,
    studentMSWorkTypeAssignmentsStatus,
    studentAssignments,
    teacherAssignments,
  ]);
};
